import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ENVIRONMENTS, type Environment } from '@zelisight/contract';

import {
  DEFAULT_PRESET,
  exactUniquesAvailable,
  isDateOnly,
  isRangePreset,
  resolveRange,
  type RangePreset,
  type ResolvedRange,
} from '@/lib/range';

/**
 * The shared environment and date range that every screen honours.
 *
 * State lives in the URL query string, not in a store. Three things fall out of
 * that for free: a link to "production, last 30 days" is shareable, the back
 * button works, and a hard refresh does not silently reset a PM to the default
 * range while they are reading a number.
 *
 * The environment is the one exception that is ALSO remembered in local
 * storage, because it is a standing choice rather than a view of the moment:
 * someone working on development wants development on every screen until they
 * say otherwise. The URL still wins when it names one, so a shared link opens
 * what it says. Only the absence of `env` falls back to the remembered value.
 */

const ENV_PARAM = 'env';
const RANGE_PARAM = 'range';
const FROM_PARAM = 'from';
const TO_PARAM = 'to';
const EXACT_PARAM = 'exact';

/** Plan 3.1: the dashboard toggle defaults to production. */
export const DEFAULT_ENV: Environment = 'production';

/** Where the last environment the user picked is remembered. */
export const ENV_STORAGE_KEY = 'zs.env';

/**
 * The parameters that describe "what am I looking at", in the order they read.
 *
 * Every one of them has to survive navigation. They are the whole filter state,
 * and because that state lives in the query string rather than a store, a link
 * that omits them is not a link to the same view: it is a link to the defaults.
 */
const FILTER_PARAMS = [ENV_PARAM, RANGE_PARAM, FROM_PARAM, TO_PARAM, EXACT_PARAM] as const;

/**
 * Carry the current filters onto an internal link.
 *
 * Without this, every link in the shell is a reset. Opening an event from the
 * Overview in development and pressing back returned production on the default
 * range, because the back link named a path and nothing else, so the target
 * page parsed an empty query string and fell through to the defaults. The same
 * was true of every item in the sidebar: choosing Sessions while looking at
 * development quietly moved you to production.
 *
 * Only the known filter keys are copied, never the whole query string. A page
 * with its own parameters, a person id or a paging cursor, must not have them
 * dragged onto an unrelated screen that would not know what to do with them.
 */
export function useFilterLink(): (path: string) => string {
  const [searchParams] = useSearchParams();
  const carried = new URLSearchParams();
  for (const key of FILTER_PARAMS) {
    const value = searchParams.get(key);
    if (value !== null && value !== '') carried.set(key, value);
  }
  const query = carried.toString();
  return (path: string) => (query === '' ? path : `${path}?${query}`);
}

function isEnvironment(value: string | null): value is Environment {
  return value !== null && (ENVIRONMENTS as readonly string[]).includes(value);
}

/**
 * The environment to show: the URL's when it names a valid one, else the one
 * the user last picked, else production. A stale or tampered stored value is
 * ignored rather than trusted, because it came from storage anyone can edit.
 */
export function resolveEnv(fromUrl: string | null, remembered: string | null): Environment {
  if (isEnvironment(fromUrl)) return fromUrl;
  if (isEnvironment(remembered)) return remembered;
  return DEFAULT_ENV;
}

function readRememberedEnv(): string | null {
  try {
    return window.localStorage.getItem(ENV_STORAGE_KEY);
  } catch {
    // Storage blocked (private mode, a locked down browser): no memory, and
    // the dashboard behaves exactly as it did before this preference existed.
    return null;
  }
}

function rememberEnv(env: Environment): void {
  try {
    window.localStorage.setItem(ENV_STORAGE_KEY, env);
  } catch {
    // As above. The choice still applies to this tab through the URL.
  }
}

export interface DashboardParams {
  env: Environment;
  range: ResolvedRange;
  /** Which preset is selected, so a refusal can suggest a genuinely shorter one. */
  preset: RangePreset;
  /** `YYYY-MM-DD`, populated only while the preset is `custom`. */
  customFrom: string;
  customTo: string;
  /** Whether the user asked for exact unique counts, plan FR-DASH-16. */
  exactUniques: boolean;
  /** False beyond seven days, where the DuckDB path is not offered. */
  exactUniquesAllowed: boolean;
  setEnv: (env: Environment) => void;
  setPreset: (preset: RangePreset) => void;
  setCustomRange: (from: string, to: string) => void;
  setExactUniques: (value: boolean) => void;
}

/** Stable clock buckets keep navigation and re-renders on the same cache key. */
function currentAnchor(refreshIntervalMs: number): number {
  return Math.floor(Date.now() / refreshIntervalMs) * refreshIntervalMs;
}

export function useDashboardParams(
  reportingTimezone: string,
  refreshIntervalMs = 60_000,
): DashboardParams {
  const [searchParams, setSearchParams] = useSearchParams();

  // Cold reports use their cache lifetime as the clock cadence. A one-minute
  // range key would bypass a five-minute staleTime and repeat expensive scans.
  const [anchorMs, setAnchorMs] = useState(() => currentAnchor(refreshIntervalMs));
  useEffect(() => {
    const id = window.setInterval(
      () => setAnchorMs(currentAnchor(refreshIntervalMs)),
      refreshIntervalMs,
    );
    return () => window.clearInterval(id);
  }, [refreshIntervalMs]);

  const env = resolveEnv(searchParams.get(ENV_PARAM), readRememberedEnv());
  const rawPreset = searchParams.get(RANGE_PARAM);
  const preset: RangePreset = isRangePreset(rawPreset) ? rawPreset : DEFAULT_PRESET;
  const customFrom = searchParams.get(FROM_PARAM) ?? '';
  const customTo = searchParams.get(TO_PARAM) ?? '';
  const exactRequested = searchParams.get(EXACT_PARAM) === '1';

  const range = useMemo(
    () =>
      resolveRange(
        preset,
        reportingTimezone,
        new Date(anchorMs),
        preset === 'custom' && isDateOnly(customFrom) && isDateOnly(customTo)
          ? { fromDate: customFrom, toDate: customTo }
          : undefined,
      ),
    [preset, reportingTimezone, anchorMs, customFrom, customTo],
  );

  const allowed = exactUniquesAvailable(range);

  const update = useCallback(
    (mutate: (next: URLSearchParams) => void) => {
      setSearchParams(
        (current) => {
          const next = new URLSearchParams(current);
          mutate(next);
          return next;
        },
        // Range and environment changes are refinements of the same view, not
        // navigation steps. Pushing them would make the back button walk
        // through every date the user tried.
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const setEnv = useCallback(
    (next: Environment) => {
      rememberEnv(next);
      update((params) => params.set(ENV_PARAM, next));
    },
    [update],
  );

  const setPreset = useCallback(
    (next: RangePreset) =>
      update((params) => {
        params.set(RANGE_PARAM, next);
        if (next !== 'custom') {
          params.delete(FROM_PARAM);
          params.delete(TO_PARAM);
        }
      }),
    [update],
  );

  const setCustomRange = useCallback(
    (from: string, to: string) =>
      update((params) => {
        params.set(RANGE_PARAM, 'custom');
        params.set(FROM_PARAM, from);
        params.set(TO_PARAM, to);
      }),
    [update],
  );

  const setExactUniques = useCallback(
    (value: boolean) =>
      update((params) => {
        if (value) params.set(EXACT_PARAM, '1');
        else params.delete(EXACT_PARAM);
      }),
    [update],
  );

  return {
    env,
    range,
    preset,
    customFrom,
    customTo,
    // A bookmarked `exact=1` on a 90 day range must not be sent: the API would
    // reject it, and the user did not ask for an error.
    exactUniques: exactRequested && allowed,
    exactUniquesAllowed: allowed,
    setEnv,
    setPreset,
    setCustomRange,
    setExactUniques,
  };
}
